import React from "react";
import {useQuery} from "@tanstack/react-query";
import OrganizationsRepository from "../repositories/OrganizationsRepository";
import {OrganizationConfiguration} from "../models/OrganizationConfiguration/OrganizationConfiguration";
import OrganizationConfigurationForm from "../components/OrganizationConfiguration/OrganizationConfigurationForm";
import {CaseMigrationStrategy} from "../models/OrganizationConfiguration/CaseMigrationStrategy";
import {MigrationStrategy} from "../models/OrganizationConfiguration/MigrationStrategy";
import {UserRole} from "../models/User";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {QUERY_KEYS} from "../constants/QueryKeys";

interface OrganizationConfigurationPageProps {
  organizationId?: string;
  userRole: UserRole;
  isAssignedUser: boolean;
}

const OrganizationConfigurationPage: React.FC<OrganizationConfigurationPageProps> = ({
  organizationId,
  userRole,
  isAssignedUser
}: OrganizationConfigurationPageProps) => {
  const organizationsRepository = new OrganizationsRepository();
  let selectedConfig = undefined;

  const {
    data: configuration,
    isLoading,
    isError,
    error,
  } = useQuery({ queryKey: [QUERY_KEYS.ORGANIZATION_CONFIGURATION, organizationId], queryFn:
    async () => {
      if (organizationId)
        return await organizationsRepository.getOrganizationConfiguration(organizationId ?? "");
    }, enabled: !!organizationId,
      refetchInterval: 2 * 60 * 1000,
      refetchIntervalInBackground: false, });

  if (isLoading)
    return (
      <div className="flex-1 min-h-0 flex flex-col gap-4 p-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-[400px] w-full rounded-md" />
      </div>
    );

  if (isError)
    return (
      <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-4">
        <p className="text-sm text-muted-foreground">Failed to load configuration.</p>
        <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );

  selectedConfig =
    !!organizationId && configuration
      ? configuration
      : {
          migrateOpenedCases: false,
          migrateClosedCases: false,
          casesOpenedAfter: null,
          casesClosedAfter: null,
          casesOpenedBefore: null,
          casesClosedBefore: null,
          casesMigrationStrategy: [
            CaseMigrationStrategy.AllCases,
            CaseMigrationStrategy.DateDescending,
          ],
          providersMigrationStrategy: MigrationStrategy.DateDescending,
          firmDocumentsMigrationStrategy: MigrationStrategy.DateDescending,
          templateFilesMigrationStrategy: MigrationStrategy.DateDescending,
          caseSpecifiedList: [],
          casePriorityList: [],
          caseIgnoredList: [],
      } as OrganizationConfiguration;

  return (
    <div className="w-full flex-1 min-h-0 overflow-y-auto">
      <OrganizationConfigurationForm
        userRole={userRole}
        organizationId={organizationId}
        configuration={selectedConfig}
        isAssignedUser={isAssignedUser}
      />
    </div>
  );
};

export default OrganizationConfigurationPage;
